import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';

import { AssignmentMessage } from '../../../../types/AssignmentMessage';
import { ContractState, type ContractMessage } from '../../../../types/ContractMessage';
import { SupplierMessage } from '../../../../types/SupplierMessage';
import { Avatar } from '../../../cdl/Avatar/Avatar';
import { Callout } from '../../../cdl/Callout/Callout';
import { queries } from '../../../common/api/queryKeys/queries';
import { translate } from '../../../common/helpers/translate.helper';
import { useEnums } from '../../../common/hooks/useEnums';
import { useRole } from '../../../common/hooks/useRole';
import { IconAlertTriangle } from '../../../common/icons/cdl/AlertTriangle';
import { IconInfoCircle } from '../../../common/icons/cdl/InfoCircle';
import { IconSpotMarket } from '../../../common/icons/SpotMarket';
import { PriceItemsBreakdown } from '../../../common/PriceItemsBreakdown/PriceItemsBreakdown';
import { Box } from '../../../common/ui/Box';
import { useAssignmentList } from '../../common/hooks/useAssignmentList';
import {
    SupplierSelectionPriceState,
    SupplierSelectionRowData,
} from '../../create-enquiry/components/SupplierSelection.types';
import { SupplierSelectionList } from '../../create-enquiry/components/SupplierSelectionList';
import { SupplierSelectionRow } from '../../create-enquiry/components/SupplierSelectionRow';
import { FormikCreateEnquiryValues } from '../../create-enquiry/CreateEnquiryFlow';
import { useSpotAvailableAtPort } from '../../create-enquiry/hooks/useSpotAvailableAtPort';
import { useSupplierSelectionPrices } from '../../create-enquiry/hooks/useSupplierSelectionPrices';
import { useUpdateSupplierDefaults } from '../../create-enquiry/hooks/useUpdateSupplierDefaults';
import { OrderMode } from '../../create-enquiry/types/OrderMode';
import { usePortDetails } from '../../hooks/usePortDetails';
import {
    getDefaultContractIdForSupplier,
    groupContractsBySupplier,
    sortContractsForSupplier,
} from '../../util/contractSelection';
import { sortSuppliersByName } from '../../util/sortSuppliersByName';

interface FormikSupplierSelectProps {
    customerId?: string;
    customerGroupId?: string;
    vesselId?: string;
    portId?: string;
    defaultSupplierId?: string;
    defaultContractId?: string;
}

export const FormikSupplierSelect = ({
    customerId,
    customerGroupId,
    vesselId,
    portId,
    defaultSupplierId,
    defaultContractId: vesselDefaultContractId,
}: FormikSupplierSelectProps) => {
    const { setFieldTouched, setFieldValue, values, errors, submitCount, touched } =
        useFormikContext<FormikCreateEnquiryValues>();
    const isDirectOrder = values.orderMode === OrderMode.DIRECT_ORDER;
    const portQuery = usePortDetails(portId);
    const spotAvailableAtPortQuery = useSpotAvailableAtPort(portQuery?.data?.country?.code, values.testOrder);
    const assignmentsQuery = useAssignmentList(customerId, portId, values.testOrder);
    const { getHumanReadableValue } = useEnums();
    const role = useRole();
    const isSpotAllowed = role.isSpotAllowed(customerId);

    const suppliers = assignmentsQuery.data
        ?.map((assignment: AssignmentMessage) => assignment.supplier)
        .filter((supplierMessage) => supplierMessage !== undefined);

    const sortedSuppliers = sortSuppliersByName(suppliers || [], defaultSupplierId);
    const supplierIds = sortedSuppliers.map((supplier) => supplier.id);
    const receiverSupplierError = errors?.receiverSupplierIds;

    const contractsQuery = useQuery({
        ...queries.contracts.pagination({
            page: 0,
            customerGroupIds: customerGroupId ? [customerGroupId] : undefined,
            customerIds: customerId ? [customerId] : undefined,
            supplierIds,
            states: [ContractState.ACTIVE],
            vesselIds: vesselId ? [vesselId] : undefined,
        }),
        enabled: !!customerId && supplierIds.length > 0,
        select: (page) => page.content,
    });

    const availableContracts = contractsQuery.data ?? [];
    const contractsBySupplierId = groupContractsBySupplier(availableContracts, customerId, vesselId);

    const contractsById = availableContracts.reduce<Record<string, ContractMessage>>((result, contract) => {
        result[contract.id] = contract;
        return result;
    }, {});
    const contractIdsBySupplierId = Object.fromEntries(
        Object.entries(contractsBySupplierId).map(([supplierId, contracts]) => [
            supplierId,
            contracts.map((contract) => contract.id),
        ])
    );
    const selectedContractIdsBySupplierId = values.receiverSupplierIds.reduce<Record<string, string>>(
        (result, supplierId) => {
            const selectedContractId = values.receiverContractIds.find(
                (contractId) => contractsById[contractId]?.supplierId === supplierId
            );

            if (selectedContractId) {
                result[supplierId] = selectedContractId;
            }

            return result;
        },
        {}
    );

    useUpdateSupplierDefaults(sortedSuppliers, defaultSupplierId, values.receiverSupplierIds);

    const { pricesBySupplierId, pricesByContractId } = useSupplierSelectionPrices({
        suppliers: sortedSuppliers,
        items: values.items,
        portId: values.portId,
        customerId: values.customerId,
        vesselId: values.vesselId,
        deliveryDate: values.dateDelivery ?? undefined,
        contractIdsBySupplierId,
        selectedContractIdsBySupplierId,
    });

    useEffect(() => {
        if (!contractsQuery.data) {
            return;
        }

        const nextReceiverContractIds = values.receiverSupplierIds.reduce<string[]>((result, supplierId) => {
            const selectedContractId = values.receiverContractIds.find(
                (contractId) => contractsById[contractId]?.supplierId === supplierId
            );
            const defaultContractIdForSupplier = getDefaultContractIdForSupplier(
                supplierId,
                contractsBySupplierId,
                defaultSupplierId,
                vesselDefaultContractId,
                (contractId) => pricesByContractId[contractId]?.totalPrice?.value
            );

            if (
                selectedContractId &&
                contractsById[selectedContractId]?.supplierId === supplierId &&
                (touched.receiverContractIds || selectedContractId === defaultContractIdForSupplier)
            ) {
                result.push(selectedContractId);
                return result;
            }

            if (defaultContractIdForSupplier) {
                result.push(defaultContractIdForSupplier);
            }

            return result;
        }, []);

        if (JSON.stringify(nextReceiverContractIds) !== JSON.stringify(values.receiverContractIds)) {
            setFieldValue('receiverContractIds', nextReceiverContractIds, false);
        }
    }, [
        contractsById,
        contractsBySupplierId,
        defaultSupplierId,
        contractsQuery.data,
        vesselDefaultContractId,
        pricesByContractId,
        setFieldValue,
        touched.receiverContractIds,
        values.receiverContractIds,
        values.receiverSupplierIds,
    ]);

    useEffect(() => {
        // turn off spot if not available
        if (spotAvailableAtPortQuery.data?.available === false || (customerId && !isSpotAllowed)) {
            setFieldValue('spot', false);
        }
    }, [customerId, isSpotAllowed, setFieldValue, spotAvailableAtPortQuery.data?.available]);

    if (assignmentsQuery.isPending) {
        if (customerId) {
            return (
                <Box marginTop={8} display="flex" flexDirection="column" gap={12}>
                    <Skeleton height={58} />
                    <Skeleton height={58} />
                    <Skeleton height={58} />
                    <Skeleton height={58} />
                </Box>
            );
        }

        return <Callout description={translate('order.missingvessel')} variant="accent" Icon={IconInfoCircle} />;
    }

    if (assignmentsQuery.isError) {
        return <Callout description={translate('global.genericError')} variant="negative" Icon={IconInfoCircle} />;
    }

    if (!sortedSuppliers.length && customerId && !isSpotAllowed) {
        return <Callout description={translate('order.nosuppliers')} variant="attention" Icon={IconInfoCircle} />;
    }

    const isSelectableSpot = isSpotAllowed && spotAvailableAtPortQuery.data?.available !== false;
    const selectableSupplierIds = sortedSuppliers.map((supplier) => supplier.id);
    const allSelectableSuppliersSelected =
        selectableSupplierIds.length > 0 &&
        selectableSupplierIds.every((supplierId) => values.receiverSupplierIds.includes(supplierId)) &&
        (!isSelectableSpot || values.spot);

    const setReceiverSupplierIds = (newIds: string[]) => {
        setFieldValue('receiverSupplierIds', newIds, true);
        setFieldTouched('receiverSupplierIds', true, false);

        const nextReceiverContractIds = newIds.reduce<string[]>((result, supplierId) => {
            const selectedContractId = values.receiverContractIds.find(
                (contractId) => contractsById[contractId]?.supplierId === supplierId
            );
            const defaultContractIdForSupplier = getDefaultContractIdForSupplier(
                supplierId,
                contractsBySupplierId,
                defaultSupplierId,
                vesselDefaultContractId,
                (contractId) => pricesByContractId[contractId]?.totalPrice?.value
            );

            if (selectedContractId) {
                result.push(selectedContractId);
                return result;
            }

            if (defaultContractIdForSupplier) {
                result.push(defaultContractIdForSupplier);
            }

            return result;
        }, []);

        setFieldValue('receiverContractIds', nextReceiverContractIds, false);
    };

    const buildSupplierRow = (supplier: SupplierMessage): SupplierSelectionRowData => {
        const isSelected = values.receiverSupplierIds.includes(supplier.id);
        const supplierContracts = contractsBySupplierId[supplier.id] ?? [];
        const sortedSupplierContracts = sortContractsForSupplier(
            supplierContracts,
            supplier.id,
            defaultSupplierId,
            vesselDefaultContractId,
            (contractId) => pricesByContractId[contractId]?.totalPrice?.value
        );
        const selectedContractId = values.receiverContractIds.find(
            (contractId) => contractsById[contractId]?.supplierId === supplier.id
        );
        const priceContractId =
            selectedContractId ??
            getDefaultContractIdForSupplier(
                supplier.id,
                contractsBySupplierId,
                defaultSupplierId,
                vesselDefaultContractId,
                (contractId) => pricesByContractId[contractId]?.totalPrice?.value
            );
        const priceData = priceContractId ? pricesByContractId[priceContractId] : pricesBySupplierId[supplier.id];
        const meta = [
            supplier.id === defaultSupplierId ? translate('labelList.default') : null,
            getHumanReadableValue('supplierGroupType', supplier.supplierGroup.type),
            sortedSupplierContracts.length === 1 ? sortedSupplierContracts[0].name : null,
        ].filter((value): value is string => !!value);

        return {
            id: supplier.id,
            title: supplier.name,
            meta,
            leadingVisual: <Avatar name={supplier.name} imageUrl={supplier.supplierGroup.logo?.thumbUrl} />,
            selected: isSelected,
            hasMultipleContracts: supplierContracts.length > 1,
            limitedTooltip: supplier.supplierGroup.emailQuote
                ? translate('order.supplierSelection.limitedTooltip')
                : undefined,
            contracts: sortedSupplierContracts,
            defaultContractId: supplier.id === defaultSupplierId ? vesselDefaultContractId : undefined,
            contractPricesByContractId: pricesByContractId,
            selectedContractId,
            onContractSelect: (contractId) => {
                const otherContractIds = values.receiverContractIds.filter(
                    (receiverContractId) => contractsById[receiverContractId]?.supplierId !== supplier.id
                );
                const nextReceiverContractIds = values.receiverSupplierIds.reduce<string[]>((result, supplierId) => {
                    if (supplierId === supplier.id) {
                        result.push(contractId);
                        return result;
                    }

                    const selectedContractId = otherContractIds.find(
                        (receiverContractId) => contractsById[receiverContractId]?.supplierId === supplierId
                    );

                    if (selectedContractId) {
                        result.push(selectedContractId);
                    }

                    return result;
                }, []);

                setFieldValue('receiverContractIds', nextReceiverContractIds, false);
                setFieldTouched('receiverContractIds', true, false);
            },
            price:
                priceData && priceData.state !== SupplierSelectionPriceState.HIDDEN
                    ? {
                          state: priceData.state,
                          label: priceData.label,
                          popoverContent: <PriceItemsBreakdown items={priceData.items ?? []} />,
                          isOutdated: priceData.isOutdated,
                          validUntil: priceData.validUntil,
                      }
                    : undefined,
            onToggle: () => {
                if (isSelected) {
                    setReceiverSupplierIds(values.receiverSupplierIds.filter((id) => id !== supplier.id));
                    return;
                }

                setReceiverSupplierIds(isDirectOrder ? [supplier.id] : [...values.receiverSupplierIds, supplier.id]);
            },
        };
    };

    const supplierRows = sortedSuppliers.map(buildSupplierRow);
    const spotRow: SupplierSelectionRowData | null =
        isSpotAllowed && !isDirectOrder
            ? {
                  id: 'spot-market',
                  title: translate('order.spotmarket'),
                  meta: [translate('order.supplierSelection.spotSubtitle')],
                  leadingVisual: (
                      <Box color="foreground.default">
                          <IconSpotMarket width={40} height={40} />
                      </Box>
                  ),
                  selected: !!values.spot,
                  disabled: spotAvailableAtPortQuery.data?.available === false,
                  onToggle: () => {
                      setFieldValue('spot', !values.spot, true);
                  },
              }
            : null;

    return (
        <Box display="flex" flexDirection="column" gap={24}>
            {submitCount > 0 && receiverSupplierError ? (
                <Callout description={receiverSupplierError} variant="attention" Icon={IconAlertTriangle} />
            ) : null}

            <Box display="flex" flexDirection="column" gap={24}>
                {spotRow ? <SupplierSelectionRow row={spotRow} /> : null}

                <SupplierSelectionList
                    rows={supplierRows}
                    onToggleAll={
                        !isDirectOrder && selectableSupplierIds.length
                            ? () => {
                                  setReceiverSupplierIds(allSelectableSuppliersSelected ? [] : selectableSupplierIds);
                                  setFieldValue(
                                      'spot',
                                      allSelectableSuppliersSelected ? false : isSelectableSpot,
                                      true
                                  );
                              }
                            : undefined
                    }
                    toggleAllLabel={
                        selectableSupplierIds.length
                            ? translate(allSelectableSuppliersSelected ? 'actions.unselectall' : 'actions.selectall')
                            : undefined
                    }
                />
            </Box>
        </Box>
    );
};
